// In-app help / onboarding content. Plain language — the people reading this
// are baristas and shop managers, not power users. `audience`:
//   'all'   — shown to everyone
//   'staff' — anyone who works shifts (employees + admins/owner)
//   'admin' — admin panel (delegated admins + owner)
// Keep steps short: a heading and one or two sentences.

export const GUIDE_SECTIONS = [
  {
    id: 'welcome',
    audience: 'all',
    icon: 'hello',
    title: 'С чего начать',
    intro: 'Это приложение держит смены, задачи и общение в одном месте.',
    steps: [
      { heading: 'Вкладки внизу', body: 'Переключайтесь между разделами кнопками внизу экрана: смена, график, чат и эта памятка.' },
      { heading: 'Имя в чате', body: 'На главном экране нажмите на своё имя вверху, чтобы указать, как вас будут видеть коллеги.' },
      { heading: 'Памятка всегда рядом', body: 'Вкладка «Памятка» никуда не денется — открывайте её в любой момент, если что-то забыли.' },
    ],
  },
  {
    id: 'shift',
    audience: 'staff',
    icon: 'flame',
    title: 'Смена',
    intro: 'Как открыть смену, закрывать задачи и сдать смену.',
    steps: [
      { heading: 'Открыть смену', body: 'В день смены на вкладке «Смена» появится кнопка «Открыть смену». Нажмите её, когда пришли на точку.' },
      { heading: 'Задачи и чек-листы', body: 'После открытия появится список задач. Нажмите на задачу, при необходимости впишите сумму/комментарий и приложите фото, затем «Готово».' },
      { heading: 'Фотоотчёты', body: 'Если задача требует фото — сделайте снимок прямо из задачи. Без обязательного фото задачу закрыть нельзя.' },
      { heading: 'Закрыть смену', body: 'Когда все обязательные задачи выполнены, нажмите «Закрыть смену». Останутся незакрытые важные задачи — приложение подскажет.' },
    ],
  },
  {
    id: 'my-schedule',
    audience: 'staff',
    icon: 'calendar',
    title: 'График — запись на смену',
    intro: 'Как самому записаться на смену.',
    steps: [
      { heading: 'Выбрать день', body: 'На вкладке «График» выберите свободный день (сегодня или позже).' },
      { heading: 'Точка и должность', body: 'Укажите точку, где вы работаете, и должность. Доступны только те точки, к которым вас прикрепил администратор.' },
      { heading: 'Отменить запись', body: 'Свою запланированную смену можно снять там же, пока она не началась.' },
    ],
  },
  {
    id: 'handover',
    audience: 'staff',
    icon: 'handshake',
    title: 'Пересменка и «проспал»',
    intro: 'Как передать смену и что делать, если не успеваешь.',
    steps: [
      { heading: 'Передать смену', body: 'Во время смены нажмите «Передать смену», выберите коллегу, пересчитайте кассу и отметьте отложенные чеки. Коллега подтверждает приём.' },
      { heading: 'Приём смены', body: 'Если смену передают вам — придёт запрос на вкладке «Смена». Проверьте кассу и нажмите «Принять».' },
      { heading: 'Проспал / не могу выйти', body: 'Как можно раньше напишите в чат администратору. Он назначит замену или отметит смену — чем раньше, тем лучше.' },
    ],
  },
  {
    id: 'chat',
    audience: 'all',
    icon: 'chat',
    title: 'Чат',
    intro: 'Общий чат точки, личные переписки и файлы.',
    steps: [
      { heading: 'Кому написать', body: 'Кнопка «Новый чат» — выбрать коллегу или создать группу. Чат точки и линия с владельцем создаются сами.' },
      { heading: 'Фото и файлы', body: 'Значок 📎 в переписке — отправить фото или файл (до 10 МБ). Фото открывается на весь экран по нажатию.' },
      { heading: 'Удалить чат', body: 'Долгое нажатие на чат в списке — «Удалить». Он пропадёт только у вас; у собеседника всё останется, и чат вернётся, если вам снова напишут.' },
      { heading: 'Уведомления', body: 'В списке чатов нажмите «Включить уведомления», чтобы получать сигнал о новых сообщениях, когда приложение свёрнуто.' },
    ],
  },
  {
    id: 'admin-overview',
    audience: 'admin',
    icon: 'grid',
    title: 'Обзор',
    intro: 'Что показывают цифры на главном экране администратора.',
    steps: [
      { heading: 'Плитки сверху', body: '«Идут сейчас» — открытые смены. «Просрочено» — задачи мимо срока. «Проспали» — смены без открытия. «В графике» — запланировано вперёд.' },
      { heading: 'Моя работа', body: 'Вы и назначенные вами админы тоже выходите на смены: разделы «Моя смена» и «Мой график» — как у сотрудника.' },
      { heading: 'Рычаги управления', body: 'Ниже — переходы в чек-листы, график, сотрудников, точки и отчёты.' },
    ],
  },
  {
    id: 'admin-checklists',
    audience: 'admin',
    icon: 'checklist',
    title: 'Чек-листы и задачи',
    intro: 'Из чего собираются задачи смены.',
    steps: [
      { heading: 'Набор задач', body: 'Создайте набор (чек-лист), привяжите его к точке и типу смены. При открытии смены задачи из набора появятся у сотрудника.' },
      { heading: 'Задача', body: 'У задачи есть название, время появления, требуется ли фото/сумма/комментарий и блокирует ли она закрытие смены.' },
      { heading: 'Изменения', body: 'Правки набора применяются к новым сменам. Уже открытые смены работают по тому, что было на момент открытия.' },
    ],
  },
  {
    id: 'admin-schedule',
    audience: 'admin',
    icon: 'calendar',
    title: 'График смен',
    intro: 'Как назначать и снимать смены.',
    steps: [
      { heading: 'Назначить', body: 'Выберите день и точку, добавьте сотрудника на смену. Сотрудник увидит её у себя.' },
      { heading: 'Пустые смены', body: 'Смену можно создать без сотрудника — её подхватит тот, кто запишется сам.' },
      { heading: 'Отменить', body: 'Отмена запланированной смены убирает её у сотрудника. Если смена уже идёт — используйте принудительное закрытие.' },
    ],
  },
  {
    id: 'admin-people',
    audience: 'admin',
    icon: 'people',
    title: 'Сотрудники',
    intro: 'Приглашения, точки, роли.',
    steps: [
      { heading: 'Пригласить', body: 'Создайте приглашение и передайте ссылку человеку. Он открывает её, входит — и появляется в списке.' },
      { heading: 'Точки и активность', body: 'Отметьте, к каким точкам прикреплён сотрудник. Неактивный сотрудник не видит операционные данные.' },
      { heading: 'Роль администратора', body: 'Можно выдать сотруднику доступ к админ-панели. Снять доступ — там же переключателем.' },
    ],
  },
  {
    id: 'admin-locations',
    audience: 'admin',
    icon: 'shop',
    title: 'Точки и типы смен',
    intro: 'Базовая настройка заведения.',
    steps: [
      { heading: 'Точка', body: 'Название, часовой пояс, должности, тип смены по умолчанию. По точкам раскидываются сотрудники и чек-листы.' },
      { heading: 'Тип смены', body: 'Задаёт длительность и нужен ли «звонок-будильник» перед открытием.' },
    ],
  },
  {
    id: 'admin-reports',
    audience: 'admin',
    icon: 'chart',
    title: 'Отчёты и отработка',
    intro: 'Что было на закрытых сменах.',
    steps: [
      { heading: 'Закрытые смены', body: 'По каждой смене — кто работал, какие задачи выполнены/пропущены, события и передачи.' },
      { heading: 'Часы', body: 'Сводка отработанных часов по сотрудникам за период.' },
    ],
  },
];

export function sectionsForRole({ isAdmin }) {
  return GUIDE_SECTIONS.filter((s) =>
    s.audience === 'all' || s.audience === 'staff' || (s.audience === 'admin' && isAdmin));
}

// Short first-run carousel — the highlights, before the full памятка.
export const INTRO_SLIDES = [
  { icon: 'hello', title: 'Добро пожаловать', body: 'Смены, задачи, чек-листы и чат — в одном приложении. Коротко о главном за минуту.' },
  { icon: 'flame', title: 'Смена', body: 'В день смены открываете её кнопкой, закрываете задачи (где-то с фото), в конце — «Закрыть смену».' },
  { icon: 'chat', title: 'Чат', body: 'Общий чат точки и личные переписки. Можно слать фото и файлы, включить уведомления.' },
  { icon: 'book', title: 'Памятка', body: 'Всё это подробно и по разделам всегда лежит во вкладке «Памятка» внизу. Загляните, если что-то забыли.' },
];
